import React, { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Nav from './Nav';
import CaseStudyToggleGroup from './CaseStudyToggleGroup';
import { getCaseStudy } from '../data/caseStudies';
import { CaseBlock } from '../types';
import styles from '../styles/CaseStudyPage.module.css';

function renderBlock(block: CaseBlock, i: number) {
  switch (block.type) {
    case 'p':
      return (
        <p key={i} className={styles.blockP}>
          {block.text}
        </p>
      );
    case 'h3':
      return (
        <h3 key={i} className={styles.blockH3}>
          {block.text}
        </h3>
      );
    case 'img': {
      const imgClass = [
        styles.blockImg,
        block.squareCorners ? styles.blockImgSquare : '',
      ]
        .filter(Boolean)
        .join(' ');
      const { href } = block;
      const imgEl = (
        <img
          className={imgClass}
          src={block.src}
          alt={block.alt}
          loading="lazy"
        />
      );
      if (href) {
        return (
          <a
            key={i}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.blockImgLink}
          >
            {imgEl}
          </a>
        );
      }
      return (
        <React.Fragment key={i}>
          {imgEl}
        </React.Fragment>
      );
    }
    case 'video':
      if (block.autoplay) {
        return (
          <video
            key={i}
            className={styles.blockVideoAutoplay}
            src={block.src}
            muted
            autoPlay
            loop
            playsInline
            preload="auto"
            aria-label="Prototype walkthrough video"
          />
        );
      }
      return (
        <video
          key={i}
          className={styles.blockVideo}
          src={block.src}
          controls
          playsInline
          preload="metadata"
        />
      );
    case 'ol':
      return (
        <ol key={i} className={styles.blockOl}>
          {block.items.map((item, j) => (
            <li key={j} className={styles.blockLi}>
              {item}
            </li>
          ))}
        </ol>
      );
    case 'figmaEmbed':
      return (
        <div key={i} className={styles.figmaWrap}>
          <iframe
            title="Figma file preview"
            className={styles.figmaFrame}
            src={`https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(
              block.url
            )}`}
            allowFullScreen
          />
        </div>
      );
    case 'toggles':
      return <CaseStudyToggleGroup key={i} items={block.items} />;
    default:
      return null;
  }
}

const CaseStudyPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const study = getCaseStudy(slug);

  useEffect(() => {
    if (study) {
      document.title = `${study.headline} — portfolio`;
    }
    return () => {
      document.title = 'Portfolio';
    };
  }, [study]);

  if (!study) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className={styles.root}>
      <Nav variant="logoOnly" />
      <div className={styles.layout}>
        <div className={styles.sidebarCol}>
          <aside className={styles.sidebar} aria-label="Case study sections">
            <Link className={styles.back} to="/#work">
              ← back
            </Link>
            <nav>
              <ul className={styles.toc}>
                {study.sections.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`}>{s.label}</a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>
        </div>

        <article className={styles.main}>
          <h1 className={styles.headline}>{study.headline}</h1>

          <div
            className={styles.heroCard}
            style={{ background: study.heroGradient }}
          >
            <div className={styles.heroInner}>
              {study.heroInner.type === 'video' ? (
                <video
                  className={styles.heroVideo}
                  src={study.heroInner.src}
                  muted
                  loop
                  playsInline
                  autoPlay
                  aria-label="Case study preview"
                />
              ) : (
                <img
                  className={styles.heroImg}
                  src={study.heroInner.src}
                  alt={study.heroInner.alt}
                />
              )}
            </div>
          </div>

          <div className={styles.metaGrid}>
            <div>
              <div className={styles.metaLabel}>role</div>
              <div className={styles.metaValue}>{study.meta.role}</div>
            </div>
            <div>
              <div className={styles.metaLabel}>the team</div>
              <div className={styles.metaValue}>{study.meta.team}</div>
            </div>
            <div>
              <div className={styles.metaLabel}>skills</div>
              <div className={styles.metaValue}>{study.meta.skills}</div>
            </div>
            <div>
              <div className={styles.metaLabel}>timeline</div>
              <div className={styles.metaValue}>{study.meta.timeline}</div>
            </div>
          </div>

          {study.sections.map((section) => (
            <section key={section.id} id={section.id} className={styles.section}>
              <h2 className={styles.sectionTitle}>{section.label}</h2>
              {section.blocks.map((b, i) => renderBlock(b, i))}
            </section>
          ))}
        </article>
      </div>
    </div>
  );
};

export default CaseStudyPage;
